import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import Dropdown from 'components/Dropdown';
import SizeChart from 'components/PdpSizeChart/SizeChart';
import PdpSpecs from 'components/PdpSpecs';
import { ATTRIBUTE_BLOCK_TYPES, ATTRIBUTE_INLINE_TYPES } from 'constants/pdp';
import RichTextProcessor from 'utils/RichTextProcessor';
import { cn } from 'utils/Tailwind';

const PdpAttributes = ({
  attributes,
  className,
  dataTestId,
  openAttributeIndex,
  pdpStorytellingEnabled,
  setCurrentAttributeIndex,
  sizeChart,
}) => {
  const attributeOptions = useMemo(
    () => ({
      renderNode: {
        [BLOCKS.EMBEDDED_ENTRY]: node => {
          switch (node.data.target.sys.contentType.sys.id) {
            case ATTRIBUTE_BLOCK_TYPES.SPECS:
              return <PdpSpecs specs={node.data.target.fields.specs} />;
            case ATTRIBUTE_BLOCK_TYPES.SIZE_CHART:
              return (
                <>
                  <div className="mb-6 max-w-3xl [&>*:last-child]:!mb-0">
                    {new RichTextProcessor(sizeChart.supportingCopy1).getReactComponents()}
                  </div>
                  <SizeChart sizeChart={sizeChart} />
                  <div className="max-w-3xl [&>*:last-child]:!mb-0">
                    {new RichTextProcessor(sizeChart.supportingCopy2).getReactComponents()}
                  </div>
                </>
              );
            default:
              return null;
          }
        },
        [INLINES.EMBEDDED_ENTRY]: node => {
          switch (node.data.target.sys.contentType.sys.id) {
            case ATTRIBUTE_INLINE_TYPES.CARBON_SCORE:
              return <>{node.data.target.fields.score}</>;
            default:
              return null;
          }
        },
      },
    }),
    [sizeChart]
  );

  const sanitizeAttributes = useMemo(
    () =>
      map(attributes, attribute => ({
        header: get(attribute, 'header'),
        attributes: new RichTextProcessor(
          attribute.content || attribute.list,
          attributeOptions
        ).getReactComponents(),
      })),
    [attributes, attributeOptions]
  );

  return (
    <section
      className={cn('PdpAttributes', className)}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      {map(sanitizeAttributes, (attribute, index) => (
        <Dropdown
          className={cn('border-b border-solid', index === 0 ? 'border-t' : 'border-t-0', {
            'border-black/20': !pdpStorytellingEnabled,
            'border-black ': pdpStorytellingEnabled,
          })}
          content={get(attribute, 'attributes')}
          dropdownHeader={get(attribute, 'header')}
          index={index}
          isOpen={openAttributeIndex === index}
          key={get(attribute, 'header')}
          onClick={setCurrentAttributeIndex}
          pdpStorytellingEnabled={pdpStorytellingEnabled}
        />
      ))}
    </section>
  );
};

PdpAttributes.propTypes = {
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      attributes: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    })
  ),
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  openAttributeIndex: PropTypes.number,
  pdpStorytellingEnabled: PropTypes.bool,
  setCurrentAttributeIndex: PropTypes.func.isRequired,
  sizeChart: PropTypes.object,
};

PdpAttributes.defaultProps = {
  attributes: [],
  className: '',
  openAttributeIndex: null,
};

export default PdpAttributes;
